// react, react-redux, redux-form
import React from "react";
import { connect } from "react-redux";
import { Field, FieldArray, FormSection, reduxForm, formValueSelector } from "redux-form";

// helpers
import { required, alphaNumeric, characterPrefixP, characterPrefixPnumber, asyncValidatePermitApplication } from "../../../helpers/validate";
import { htmlEncodeIds, jumpToError } from "../../../helpers/renderHelpers";

// redux actions
import { updateAddress } from "../../../actions/formActions";

// render components
import RenderField from "../../global/RenderField";
import RenderOwners from "../../global/RenderOwners";
import Profile from "../../global/Profile";
import AddressSuggestion from "../../global/AddressSuggestion/AddressSuggestion";

class Contacts extends React.Component {

  shouldComponentUpdate(nextProps, nextState) {
    return JSON.stringify(this.props) !== JSON.stringify(nextProps) || JSON.stringify(this.state) !== JSON.stringify(nextState);
  }

  render() {
    const { formName, handleSubmit, primaryContact, applicant, isCommonPropertyInvolved, applicantSameAsPrimaryContact, sameAsPrimaryContactApplicant, preApplicationDiscussion, addresses, appState, asyncValidating, mapShareData } = this.props;

    // Contact field names
    const profileFormFieldNames = ['title', 'givenName', 'familyName', 'abn', 'organisationName', 'positionTitle', 'email', 'telephone', 'mobile']
    const addressFormFieldNames = ['country', 'postcode', 'state', 'suburb', 'streetName', 'streetNo', 'unitNo']

    let primaryAddress, applicantAddress, ownerAddress, ownersCorpManagerAddress;
    addressFormFieldNames.forEach(name => primaryAddress = Object.assign({[name]: "primaryContact.address." + name}, primaryAddress))
    addressFormFieldNames.forEach(name => applicantAddress = Object.assign({[name]: "applicant.address." + name}, applicantAddress))
    addressFormFieldNames.forEach(name => ownerAddress = Object.assign({[name]: "owners[0].address." + name}, ownerAddress))
    addressFormFieldNames.forEach(name => ownersCorpManagerAddress = Object.assign({[name]: "ownersCorpManagerDetails.address." + name}, ownersCorpManagerAddress))
    
    // Applicant is the same as primary contact
    
    if ( applicantSameAsPrimaryContact && applicantSameAsPrimaryContact.length && primaryContact ) {

      profileFormFieldNames.forEach(name => this.props.change(`applicant[${name}]`, primaryContact[name] || ""))

      if ( primaryContact.address ) {
        addressFormFieldNames.forEach(name => this.props.change(`applicant.address[${name}]`, primaryContact.address[name] || ""))
      }

      const primaryContactAddressId = htmlEncodeIds(JSON.stringify({ primaryAddress }).replace('primaryAddress', ''));

      const primaryContactAddress = addresses.find(address => address.id === primaryContactAddressId);

      if ( primaryContactAddress ) { 
        
        const applicationAddressId = htmlEncodeIds(JSON.stringify({ applicantAddress }).replace('applicantAddress', ''));

        this.props.change('applicant.address.' + applicationAddressId + "-manual", primaryContact.address[primaryContactAddressId + "-manual"]);

        this.props.updateAddress(
          applicationAddressId, 
          primaryContactAddress.value, 
          primaryContactAddress.isSelected,
          primaryContactAddress.isManual,
          primaryContactAddress.required
        )
      }
    }

    // Owner #1 is the same as primary contact

    if ( sameAsPrimaryContactApplicant && sameAsPrimaryContactApplicant === "primaryContact" && primaryContact ) {

      profileFormFieldNames.forEach(name => this.props.change(`owners[0][${name}]`, primaryContact[name] || ""))

      if ( primaryContact.address ) {
        addressFormFieldNames.forEach(name => this.props.change(`owners[0].address[${name}]`, primaryContact.address[name] || ""))
      }

      const primaryContactAddressId = htmlEncodeIds(JSON.stringify({ primaryAddress }).replace('primaryAddress', ''));

      const primaryContactAddress = addresses.find(address => address.id === primaryContactAddressId);

      if ( primaryContactAddress ) { 
        
        const ownerAddressId = htmlEncodeIds(JSON.stringify({ ownerAddress }).replace('ownerAddress', ''));

        this.props.change('owners[0].address.' + ownerAddressId + "-manual", primaryContact.address[primaryContactAddressId + "-manual"]);

        this.props.updateAddress(
          ownerAddressId, 
          primaryContactAddress.value, 
          primaryContactAddress.isSelected,
          primaryContactAddress.isManual,
          primaryContactAddress.required
        )
      }
    }
    
    // Owner #1 is the same as applicant

    if ( sameAsPrimaryContactApplicant && sameAsPrimaryContactApplicant === "applicant" && typeof applicant !== "undefined" ) {

      profileFormFieldNames.forEach(name => this.props.change(`owners[0][${name}]`, applicant[name] || ""))

      if ( applicant.address ) {
        addressFormFieldNames.forEach(name => this.props.change(`owners[0].address[${name}]`, applicant.address[name] || ""))
      }

      const applicantAddressId = htmlEncodeIds(JSON.stringify({ applicantAddress }).replace('applicantAddress', ''));

      const applicantContactAddress = addresses.find(address => address.id === applicantAddressId);

      if ( applicantContactAddress ) { 
        
        const ownerAddressId = htmlEncodeIds(JSON.stringify({ ownerAddress }).replace('ownerAddress', ''));

        this.props.change('owners[0].address.' + ownerAddressId + "-manual", applicant.address[applicantAddressId + "-manual"]);
        
        this.props.updateAddress(
          ownerAddressId, 
          applicantContactAddress.value,
          applicantContactAddress.isSelected,
          applicantContactAddress.isManual,
          applicantContactAddress.required
        )
      }
    }

    // Passed autocomplete address
    const passed = mapShareData === false || this.props.addresses.length === 0 || this.props.addresses.find(address => (address.isSelected === true && address.isManual === false) || (address.isSelected === false && address.isManual === true)) !== undefined ? true : false;

    return (
      <form id={formName} onSubmit={handleSubmit}>
        <div>
          <div className="sq-form-section-description">
            <h2>Pre-application meeting</h2>
          </div>
          <div className="sq-form-section sq-form-section-no-padding">
            <Field
              name="preApplicationDiscussion"
              type="radiobox"
              label="Have you had a pre-application discussion with Heritage Victoria in relation to this application?"
              note="It is highly recommended that you do consult with Heritage Victoria prior to filling out this application."
              component={RenderField}
              required
              templateList={[
                { name: "Yes", value: "true" },
                { name: "No", value: "false" }
              ]}
            />
            {preApplicationDiscussion && preApplicationDiscussion.length > 0 && preApplicationDiscussion === "true" && (  
              <Field
                name="preApplicationEventNumber"
                type="text"
                required
                component={RenderField}
                label="What was the P-Number allocated to the pre-application discussion?"
                validate={[required, alphaNumeric, characterPrefixP, characterPrefixPnumber]}
              />
            )}
          </div>
        </div>
        <hr />  

        <h2>Contact details</h2>
        <p>We'll need contact details for the following people:</p>
        <ul>
          <li><b>Primary Contact details</b></li>
          <li><b>Applicant details</b></li>
          <li><b>Owner(s) details</b></li>
          <li>Owners corporation manager (if applicable)</li>
        </ul>     
        <div id="primaryContact">
          <div className="sq-form-section-description">
            <h3>Primary contact</h3>
            <p>We will follow up with this person if we require further application details. The primary contact will be cc-ed into all correspondence from Heritage Victoria in relation to this application.</p>
          </div>
          <FormSection name="primaryContact">
            <Profile form={formName} formData={appState.form[formName].values} section="primaryContact"/>
            <AddressSuggestion required={true} form={this} fields={primaryAddress} />
          </FormSection>
        </div>
        <div id="applicant">
          <div className="sq-form-section-description">
            <h3>Applicant</h3>
            <p>The Permit will be issued to this person.</p>
          </div>
          <Field
            name="sameAsPrimaryContact"
            type="checkbox"
            component={RenderField}
            templateList={[{ name: "Same as primary contact details", value: "true" }]}
          />
          <FormSection name="applicant" className={(!applicantSameAsPrimaryContact || (applicantSameAsPrimaryContact && applicantSameAsPrimaryContact.length === 0)) ? '' : 'hidden'}>
            <Profile form={formName} formData={appState.form[formName].values} section="applicant"/>
            <AddressSuggestion required={true} form={this} fields={applicantAddress} />
          </FormSection>
        </div>
        <div id="ownerDetailsWrapper">
          <div className="sq-form-section-description">
            <h3>Owner(s)</h3>
            <p>Owner's consent to the proposed works must be demonstrated before this application can be assessed.</p>
          </div>
          <FieldArray name="owners" component={RenderOwners} props={{form: this, appState: appState}} />
          {((appState.form[formName].values.owners.length === 1 && (!appState.form[formName].values.sameAsPrimaryContact || (appState.form[formName].values.sameAsPrimaryContact && appState.form[formName].values.sameAsPrimaryContact.length === 0) || !appState.form[formName].values.sameAsPrimaryContactApplicant)) || (appState.form[formName].values.owners.length > 1)) && (
            <div className="sq-form-section">
              <ul className="list">
                <li className="list-tile info">
                  <div className="list-tile-content">
                    <p>You will be required to upload a written consent letter later in this form {(appState.form[formName].values.owners.length > 1 && (!appState.form[formName].values.sameAsPrimaryContact || (appState.form[formName].values.sameAsPrimaryContact && appState.form[formName].values.sameAsPrimaryContact.length === 0) || !appState.form[formName].values.sameAsPrimaryContactApplicant)) || (appState.form[formName].values.sameAsPrimaryContact && appState.form[formName].values.sameAsPrimaryContactApplicant && appState.form[formName].values.owners.length > 2)  ? 'for each owner listed below:' : 'from owner:'}</p>
                    <ul className="list-tile-content">
                      {appState.form[formName].values.sameAsPrimaryContact && appState.form[formName].values.sameAsPrimaryContact.length > 0 && appState.form[formName].values.sameAsPrimaryContactApplicant ? appState.form[formName].values.owners.slice(1).map((owner, i) =>
                        <li key={i}>{owner.givenName || owner.familyName ? `${owner.title ? owner.title : ''} ${owner.givenName ? owner.givenName : ''} ${owner.familyName ? owner.familyName : ''}` : `Owner #${i + 2}`}</li>
                      ) : appState.form[formName].values.owners.map((owner, i) =>
                        <li key={i}>{owner.givenName || owner.familyName ? `${owner.title ? owner.title : ''} ${owner.givenName ? owner.givenName : ''} ${owner.familyName ? owner.familyName : ''}` : `Owner #${i + 1}`}</li>
                      )}
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div id="ownersCorporation">
          <div className="sq-form-section-description">
            <h3>Owners corporation manager</h3>
            <p>If the works involve the common property, owners corporation consent is required.</p>
          </div>
          <Field
            name="isCommonPropertyInvolved"
            type="radiobox"
            label="Do the works involve common property?"
            component={RenderField}
            required
            templateList={[
              { name: "Yes", value: "true" },
              { name: "No", value: "false" }
            ]}
          />
          {isCommonPropertyInvolved && isCommonPropertyInvolved === "true" && (
            <FormSection name="ownersCorpManagerDetails">
              <Profile form={formName} formData={appState.form[formName].values} section="ownersCorpManagerDetails"/>
              <AddressSuggestion required={true} form={this} fields={ownersCorpManagerAddress} />
            </FormSection>
          )}
          {isCommonPropertyInvolved && isCommonPropertyInvolved === "true" && (
            <div className="sq-form-section">
              <ul className="list">
                <li className="list-tile info">
                  <div className="list-tile-content">
                    <p>You will be required to upload a written consent letter later in this form from the owners corporation manager{(appState.form[formName].values.ownersCorpManagerDetails.givenName || appState.form[formName].values.ownersCorpManagerDetails.familyName) && (<b> ({appState.form[formName].values.ownersCorpManagerDetails.title && `${appState.form[formName].values.ownersCorpManagerDetails.title} `}{appState.form[formName].values.ownersCorpManagerDetails.givenName && appState.form[formName].values.ownersCorpManagerDetails.givenName}{appState.form[formName].values.ownersCorpManagerDetails.familyName && ` ${appState.form[formName].values.ownersCorpManagerDetails.familyName}`})</b>)}.</p>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="sq-form-control">
          <button type="submit" className={`sq-form-page-button next ${asyncValidating ? 'async-validating' : ''}`} onClick={jumpToError} disabled={!passed || asyncValidating}>Next</button>
        </div>
      </form>
    );
  }
}

const mapStateToProps = (state, formName) => {
  const selector = formValueSelector(formName.formName); // <-- same as form name
  let returnObj = {};
  const applicantSameAsPrimaryContact = selector(state, "sameAsPrimaryContact");
  const sameAsPrimaryContactApplicant = selector(state, "sameAsPrimaryContactApplicant");
  const isCommonPropertyInvolved = selector(state, "isCommonPropertyInvolved");
  const preApplicationDiscussion = selector(state, "preApplicationDiscussion");

  const primaryContactAddressIsManual = 'primaryContact.address.' + htmlEncodeIds(JSON.stringify({
      unitNo: 'primaryContact.address.unitNo',
      streetNo: 'primaryContact.address.streetNo',
      streetName: 'primaryContact.address.streetName',
      suburb: 'primaryContact.address.suburb',
      state: 'primaryContact.address.state',
      postcode: 'primaryContact.address.postcode',
      country: 'primaryContact.address.country'
  })) + '-manual';

  const applicantAddressIsManual = 'applicant.address.' + htmlEncodeIds(JSON.stringify({
      unitNo: 'applicant.address.unitNo',
      streetNo: 'applicant.address.streetNo',
      streetName: 'applicant.address.streetName',
      suburb: 'applicant.address.suburb',
      state: 'applicant.address.state',
      postcode: 'applicant.address.postcode',
      country: 'applicant.address.country'
  })) + '-manual';

  returnObj = selector(
    state,
    "primaryContact.title",
    "primaryContact.givenName",
    "primaryContact.familyName",
    "primaryContact.abn",
    "primaryContact.organisationName",
    "primaryContact.positionTitle",
    "primaryContact.email",
    "primaryContact.telephone",
    "primaryContact.mobile",
    "primaryContact.address.unitNo",
    "primaryContact.address.streetNo",
    "primaryContact.address.streetName",
    "primaryContact.address.suburb",
    "primaryContact.address.state",
    "primaryContact.address.postcode",
    "primaryContact.address.country",
    primaryContactAddressIsManual,
    "applicant.title",
    "applicant.givenName",
    "applicant.familyName",
    "applicant.abn",
    "applicant.organisationName",
    "applicant.positionTitle",
    "applicant.email",
    "applicant.telephone",
    "applicant.mobile",
    "applicant.address.unitNo",
    "applicant.address.streetNo",
    "applicant.address.streetName",
    "applicant.address.suburb",
    "applicant.address.state",
    "applicant.address.postcode",
    "applicant.address.country",
    applicantAddressIsManual,
    "owners[0].title",
    "owners[0].givenName",
    "owners[0].familyName",
    "owners[0].abn",
    "owners[0].organisationName",
    "owners[0].positionTitle",
    "owners[0].email",
    "owners[0].telephone",
    "owners[0].mobile",
    "owners[0].address.unitNo",
    "owners[0].address.streetNo",
    "owners[0].address.streetName",
    "owners[0].address.suburb",
    "owners[0].address.state",
    "owners[0].address.postcode",
    "owners[0].address.country"
  );

  if (applicantSameAsPrimaryContact) {
    returnObj.applicantSameAsPrimaryContact = applicantSameAsPrimaryContact;
  }
  if (sameAsPrimaryContactApplicant && sameAsPrimaryContactApplicant.length) {
    returnObj.sameAsPrimaryContactApplicant = sameAsPrimaryContactApplicant;
  }
  if (isCommonPropertyInvolved) {
    returnObj.isCommonPropertyInvolved = isCommonPropertyInvolved;
  }
  if (preApplicationDiscussion) {
    returnObj.preApplicationDiscussion = preApplicationDiscussion;
  }
  returnObj.addresses = state.service.addresses;
  returnObj.mapShareData = state.service.mapShareData;

  returnObj.appState = state;
  return returnObj;
};

const mapDispatchToProps = {
  updateAddress
};

const form = reduxForm({
  destroyOnUnmount: false, //        <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  asyncValidate: asyncValidatePermitApplication,
  asyncBlurFields: ['preApplicationEventNumber']  
});

export default connect(mapStateToProps, mapDispatchToProps)((form)(Contacts));